interface Props {
  status: string;
  tone?: "default" | "success" | "info" | "warning" | "danger" | "muted";
}

// We hard-code green for `success` rather than reusing the `accent` palette
// because accent is the brand blue (#0e68a8) — paid badges need to read
// unambiguously green, and open/in-progress badges read blue.
const toneClasses: Record<NonNullable<Props["tone"]>, string> = {
  default: "bg-ink-100 text-ink-700",
  success: "bg-emerald-100 text-emerald-800",
  info: "bg-accent-100 text-accent-800",
  warning: "bg-amber-100 text-amber-800",
  danger: "bg-red-100 text-red-800",
  muted: "bg-ink-50 text-ink-500",
};

export function StatusBadge({ status, tone = "default" }: Props) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium uppercase tracking-wide ${toneClasses[tone]}`}
    >
      {status}
    </span>
  );
}

export function statusToTone(status?: string | null): NonNullable<Props["tone"]> {
  switch (status) {
    // Green — settled / running successfully
    case "active":
    case "paid":
    case "succeeded":
    case "trialing":
      return "success";
    // Blue — finalized but not yet paid / in-progress
    case "open":
    case "in_progress":
    case "in_review":
      return "info";
    // Amber — needs attention
    case "past_due":
    case "pending":
    case "pending_payment":
    case "failed":
    case "waiting_on_client":
      return "warning";
    // Red — terminal / cancelled
    case "void":
    case "cancelled":
    case "canceled":
    case "suspended":
    case "uncollectible":
    case "refunded":
      return "danger";
    case "no_subscription":
    case "draft":
      return "muted";
    default:
      return "default";
  }
}
